import { getMCPMonitorGrowthRate } from "@/api/endpoints/searches";
import { SENTIMENT_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { formatDateForApi } from "@/utils";
import { CardTitle } from "@components/ui/card";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetStat,
  WidgetTitle,
  WidgetTrend,
} from "@components/ui/widget";
import {
  getMCPMonitorFiltersFromSearchFilterV4,
  getOverallSentiment,
} from "@components/widgets/utils";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";

export default function SentimentsWidgets({
  diffInDays,
  searchUuid,
  filters,
}: {
  filters: SearchFilterV4WithDates;
  diffInDays: number;
  searchUuid: Uuid;
}) {
  const { abbreviateNumber } = useI18n();

  // Server state
  const {
    data: sentimentDistribution,
    isLoading: isSentimentDistributionLoading,
  } = useQuery({
    queryKey: [
      searchUuid,
      "insights-sentiment",
      "sentiment-growth-rate",
      filters,
    ],
    queryFn: () =>
      getMCPMonitorGrowthRate(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        compare_start_date: formatDateForApi(
          subDays(new Date(filters.start_date), diffInDays + 1)
        ),
        compare_end_date: formatDateForApi(
          subDays(new Date(filters.end_date), diffInDays + 1)
        ),
        metric: "sentiment",
      }),
  });

  const shares = sentimentDistribution?.ok
    ? {
        positive: sentimentDistribution.data.positive.value,
        negative: sentimentDistribution.data.negative.value,
        neutral: sentimentDistribution.data.neutral.value,
      }
    : undefined;

  const changePct = sentimentDistribution?.ok
    ? {
        positive: sentimentDistribution.data.positive.change_pct,
        negative: sentimentDistribution.data.negative.change_pct,
        neutral: sentimentDistribution.data.neutral.change_pct,
      }
    : undefined;

  const overallSentiment = shares ? getOverallSentiment(shares) : undefined;

  return (
    <>
      <Widget className="col-span-12 @3xl/grid:col-span-6 @4xl/grid:col-span-3 h-24">
        <WidgetHeader
          isLoading={isSentimentDistributionLoading}
          skeletonClassName="h-7"
        >
          <WidgetTitle>
            <Trans>Övergripande sentiment</Trans>
          </WidgetTitle>

          {overallSentiment && (
            <CardTitle className="text-xl font-semibold">
              {i18n._(SENTIMENT_TO_LABEL[overallSentiment])}
            </CardTitle>
          )}
        </WidgetHeader>
      </Widget>

      <Widget className="col-span-12 @3xl/grid:col-span-6 @4xl/grid:col-span-3 bg-green-50 h-24 gap-0.5">
        <WidgetHeader
          isLoading={isSentimentDistributionLoading}
          skeletonClassName="h-7"
        >
          <WidgetTitle>
            <Trans>Positiva</Trans>
          </WidgetTitle>

          {shares && (
            <WidgetStat>{abbreviateNumber(shares.positive)}</WidgetStat>
          )}
        </WidgetHeader>
        <WidgetContent isLoading={isSentimentDistributionLoading}>
          {changePct && (
            <WidgetTrend
              value={
                changePct?.positive ? changePct?.positive / 100 : undefined
              }
            />
          )}
        </WidgetContent>
      </Widget>

      <Widget className="col-span-12 @3xl/grid:col-span-6 @4xl/grid:col-span-3 bg-red-50 h-24 gap-0.5">
        <WidgetHeader
          isLoading={isSentimentDistributionLoading}
          skeletonClassName="h-7"
        >
          <WidgetTitle>
            <Trans>Negativa</Trans>
          </WidgetTitle>

          {shares && (
            <WidgetStat>{abbreviateNumber(shares.negative)}</WidgetStat>
          )}
        </WidgetHeader>
        <WidgetContent isLoading={isSentimentDistributionLoading}>
          {changePct && (
            <WidgetTrend
              value={changePct?.negative ? changePct.negative / 100 : undefined}
            />
          )}
        </WidgetContent>
      </Widget>

      <Widget className="col-span-12 @3xl/grid:col-span-6 @4xl/grid:col-span-3 h-24 gap-0.5">
        <WidgetHeader
          isLoading={isSentimentDistributionLoading}
          skeletonClassName="h-7"
        >
          <WidgetTitle>
            <Trans>Neutrala</Trans>
          </WidgetTitle>

          {shares && (
            <WidgetStat>{abbreviateNumber(shares.neutral)}</WidgetStat>
          )}
        </WidgetHeader>
        <WidgetContent isLoading={isSentimentDistributionLoading}>
          {changePct && (
            <WidgetTrend
              value={changePct?.neutral ? changePct.neutral / 100 : undefined}
            />
          )}
        </WidgetContent>
      </Widget>
    </>
  );
}
